import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useRouter } from "next/router";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import SelectResourceScreen from "./SelectResourceScreen";
import SelectDateTimeScreen from "./SelectDateTimeScreen";
import SelectCapacityScreen from "./SelectCapacityScreen";
import SearchResult from "./components/SearchResult";

import { getAllAvailableResources } from "../../redux/resources/ResourcesActions";
import { formatDateTime, setHeaderSpace } from "../../utils/CommonFunctions";
import SearchLoading from "./SearchLoading";
import styles from "../../styles/sass/search.module.scss";

const SearchResource = () => {
  const router = useRouter();
  const { query, asPath } = router;
  const search = useMemo(() => asPath, [asPath]);

  const dispatch = useDispatch();
  const prevProps = useRef();

  const [state, setState] = useState({});

  const activeSpace = useSelector(
    ({ workspace }) => workspace.workSpaceMap?.[query.spaceId]
  );

  const availableResources = useSelector(
    ({ resources }) => resources.availableResources
  );

  const onNext = useCallback((search) => search && router.push(search));
  const onBack = useCallback(() => router.back());

  const setBookingObj = (params) => {
    const space_id = params?.spaceId;
    const resource_type = params?.resource_type || "";
    const capacity = (params?.capacity && Number(params?.capacity)) || "";
    const from_to_date =
      params?.from_date && params?.to_date
        ? [moment(params?.from_date), moment(params?.to_date)]
        : null;
    const from_date = (params?.from_date && moment(params?.from_date)) || "";
    const to_date = (params?.to_date && moment(params?.to_date)) || "";
    let from_time;
    let duration;

    if (resource_type === "meeting_room") {
      const fromHour = params?.from_time?.split(":")?.[0];
      const fromMinute = params?.from_time?.split(":")?.[1];
      from_time = moment(from_date).set({
        hour: fromHour,
        minute: fromMinute,
        second: 0,
      });

      duration = params?.duration && Number(params?.duration);
    }

    return {
      space_id,
      resource_type,
      capacity,
      from_date,
      from_time,
      duration,
      to_date,
      from_to_date,
    };
  };

  const fetchAvailbleResources = useCallback(() => {
    const {
      spaceId,
      resource_type,
      capacity,
      from_date,
      to_date,
      from_time,
      duration,
    } = query;

    const newState = {
      space_id: spaceId,
      resource_type: resource_type || "",
      capacity: (capacity && Number(capacity)) || "",
      from_date: "",
      from_time: "",
      duration: "",
      to_date: "",
    };

    if (from_date && moment(from_date).isValid()) {
      newState.from_date = moment(from_date);
    }

    if (resource_type === "meeting_room") {
      if (from_time) {
        const fromHour = from_time?.split(":")?.[0];
        const fromMinute = from_time?.split(":")?.[1];
        newState.from_time = moment(from_date).set({
          hour: fromHour,
          minute: fromMinute,
          second: 0,
        });
      }

      if (duration) {
        newState.duration = Number(duration) || "";
      }
    } else {
      if (to_date && moment(to_date).isValid()) {
        newState.to_date = moment(to_date) || "";
      }
    }

    if (JSON.stringify(prevProps?.current?.search) !== JSON.stringify(search)) {
      setState((preState) => ({
        ...preState,
        ...newState,
      }));
    }

    // call to api to fetch all the available resources
    if (
      newState?.space_id &&
      newState?.resource_type &&
      newState?.capacity &&
      activeSpace
    ) {
      let startDateTime;
      let endDateTime;

      if (resource_type === "meeting_room") {
        if (!newState?.from_date || !newState?.from_time || !newState?.duration)
          return;

        startDateTime = moment(`${from_date} ${from_time}`);
        endDateTime = moment(startDateTime).add(duration, "hours");
      } else {
        if (!newState?.from_date || !newState?.to_date) return;

        const startOfficeHour = activeSpace?.office_hours?.[0];
        const endOfficeHour = activeSpace?.office_hours?.[1];

        startDateTime = moment(newState?.from_date).set({
          hour: moment(startOfficeHour).format("HH"),
          minute: moment(startOfficeHour).format("mm"),
        });
        endDateTime = moment(newState?.to_date).set({
          hour: moment(endOfficeHour).format("HH"),
          minute: moment(endOfficeHour).format("mm"),
        });
      }

      const availableResParams = {
        from: startDateTime.utc().toDate(),
        to: endDateTime.utc().toDate(),
        capacity: newState?.capacity,
        space_id: newState?.space_id,
        type: newState?.resource_type,
      };
      const filter = JSON.stringify(availableResParams);

      if (filter && filter !== state.filter) {
        setState((preState) => ({
          ...preState,
          filter,
        }));

        dispatch(getAllAvailableResources(availableResParams));
      }
    } else {
      if (state.filter) {
        setState((preState) => ({
          ...preState,
          filter: null,
        }));
      }
    }
  }, [activeSpace, dispatch, query, search]);

  const updateUrlParameter = useCallback((url, param, value) => {
    const regex = new RegExp("(?<=[?|&])(" + param + "=)[^&]+", "i");
    return url.replace(regex, param + "=" + value);
  }, []);

  const onChangeWorkspace = useCallback(
    (event) => {
      const newUrl = updateUrlParameter(router.asPath, "spaceId", event);
      onNext(newUrl);
      setState((preState) => ({
        ...preState,
        space_id: event,
      }));
    },
    [updateUrlParameter, router.asPath]
  );

  const handleChange = useCallback(
    (name) => (event) => {
      const value = event?.target?.value ?? event;

      if (name === "workspace") {
        onChangeWorkspace(event);
      } else {
        setState((preState) => ({
          ...preState,
          [name]: value,
        }));
      }
    },
    []
  );

  const onSearch = useCallback(() => {
    let {
      space_id,
      resource_type,
      capacity,
      to_date,
      from_date,
      from_time,
      duration,
    } = state;

    // call to api to fetch all the available resources
    if (resource_type && capacity > 0) {
      let search = `?spaceId=${space_id}`;
      search = `${search}&resource_type=${resource_type}`;

      if (
        resource_type === "meeting_room" &&
        from_date &&
        from_time &&
        duration
      ) {
        from_date = formatDateTime(from_date, "d");
        from_time = formatDateTime(from_time, "t");
        search = `${search}&from_date=${from_date}`;
        search = `${search}&from_time=${from_time}`;
        search = `${search}&duration=${duration}`;
      } else if (resource_type !== "meeting_room" && from_date && to_date) {
        search = `${search}&from_date=${formatDateTime(from_date, "d")}`;
        search = `${search}&to_date=${formatDateTime(to_date, "d")}`;
      }
      search = `${search}&capacity=${capacity}`;
      onNext(search);
    }
  }, [onNext, state]);

  useEffect(() => {
    // setState((preState) => ({
    //   ...preState,
    //   ...setBookingObj(query),
    // }));
    fetchAvailbleResources();

    return () => {
      prevProps.current = { search };
    };
  }, [fetchAvailbleResources, query, search]);

  const onBookNow = useCallback(
    (resource_id, plan_id) => {
      const { filter } = state;

      let exp_timestamp = new Date();
      exp_timestamp.setMinutes(exp_timestamp.getMinutes() + 30);

      const bookResource = {
        ...setBookingObj(query),
        exp_timestamp: exp_timestamp,
        resource_id,
        plan_id,
        filter,
      };
      /** member Login flow
       *
       let url = `${REACT_APP_BOOKING_URL}?bookResource=${JSON.stringify(
         bookResource
         )}`;
         window.open(url, "_self");

       */

      //Redirect to Boking Form
      localStorage.setItem("bookResource", JSON.stringify(bookResource));
      router.push("/booking-request");
    },
    [router, query, state]
  );

  const {
    space_id,
    filter,
    resource_type,
    capacity,
    to_date,
    from_date,
    from_time,
    duration,
  } = state;
  const mAvailableResources = availableResources?.[filter];

  /* for set header */
  useEffect(() => {
    setHeaderSpace();
  }, []);

  return (
    <>
        <div className={`${styles["booking-container-outer-box"]}`}>
          <div id="setSpacer" className="setBanner">
            <div className="bannerInner">
              {!resource_type && <SelectResourceScreen onNext={onNext} />}

              {resource_type && !(from_date || (to_date && from_time && duration)) && (
                <SelectDateTimeScreen
                  onNext={onNext}
                  onBack={onBack}
                  resource_type={resource_type}
                />
              )}

              {resource_type &&
                (from_date || (from_date && from_time && duration)) &&
                !capacity && (
                  <SelectCapacityScreen
                    onNext={onNext}
                    onBack={onBack}
                    resource_type={resource_type}
                  />
                )}

              {filter && mAvailableResources?.loading && (
                <SearchLoading/>
              )}

              {filter && !mAvailableResources?.loading && (
                  <SearchResult
                    workspace={space_id}
                    resource_type={resource_type}
                    capacity={capacity}
                    to_date={to_date}
                    from_date={from_date}
                    from_time={from_time}
                    duration={duration}
                    filter={filter}
                    onSearch={onSearch}
                    handleChange={handleChange}
                    availableResources={mAvailableResources?.list}
                    loading={mAvailableResources?.loading}
                    onBookNow={onBookNow}
                  />
                )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchResource;
